import {Component, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from "@angular/router";
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from "devextreme-angular";
import {
    DxiColumnComponent, DxiItemComponent, DxoColumnChooserComponent, DxoExportComponent, DxoFilterRowComponent,
    DxoHeaderFilterComponent, DxoLoadPanelComponent, DxoPagerComponent,
    DxoPagingComponent, DxoPositionComponent,
    DxoScrollingComponent, DxoSearchComponent, DxoSearchPanelComponent, DxoSelectionComponent, DxoSortingComponent,
    DxoToolbarComponent
} from "devextreme-angular/ui/nested";
import {FilterConfig, GridPageComponent} from "../../common/grid-page.component";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import {
    AssetCondition, AssetClassification,
    AssetGridViewDto,
    AssetOwnership,
    AssetType, AuthService, PermissionType, AccessType
} from "@services";
import EnumUtils from "../../../shared/enum-utils";
import {DxDataGridTypes} from "devextreme-angular/ui/data-grid";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {AssetTypePipe} from "../../../pipes/asset-type.pipe";
import {AssetClassificationPipe} from "../../../pipes/asset-classification.pipe";

@Component({
    selector: 'app-assets',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxButtonComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSearchPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        DxFormComponent,
        FilterPanelComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxoScrollingComponent,
        CapitalCasePipe,
        RouterLink,
        AssetTypePipe,
        AssetClassificationPipe,
    ],
    providers: [AssetTypePipe],
    templateUrl: './assets.component.html',
    styleUrl: './assets.component.scss',
})
export class AssetsComponent extends GridPageComponent<AssetGridViewDto> {
    private router = inject(Router);
    private assetTypePipe = inject(AssetTypePipe);

    protected authService = inject(AuthService);

    constructor() {
        super();
        this.init(AssetGridViewDto);
    }

    dataSource = this.dataSources.assets();
    assetTypeOptions = EnumUtils.getEnumOptions(AssetType, (v) => this.assetTypePipe.transform(v));
    assetOwnershipOptions = EnumUtils.getEnumOptions(AssetOwnership);
    assetConditionOptions = EnumUtils.getEnumOptions(AssetCondition);
    assetClassificationOptions = EnumUtils.getEnumOptions(AssetClassification);

    protected override exportName: string = "Assets";

    protected override filterConfig: FilterConfig[] = [
        {field: 'tagId', op: 'contains'},
        {field: 'type', op: '='},
        {field: 'brand', op: 'contains'},
        {field: 'model', op: 'contains'},
        {field: 'serial', op: 'contains'},
        {field: 'ownership', op: '='},
        {field: 'agreement', op: 'contains'},
        {field: 'vendor', op: 'contains'},
        {field: 'effectiveDate', op: '>='},
        {field: 'endDate', op: '>='},
        {field: 'condition', op: '='},
        {field: 'classification', op: '='},
        {field: 'businessUnitLocation', op: 'contains'},
        {field: 'created', op: '>='},
    ];

    async addAsset() {
        await this.router.navigate(['/assets/create']);
    }

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const {data} = e;
        await this.router.navigate([`/assets/edit/${data.id}`]);
    }

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;

    protected getBadgeClass(status: AssetCondition): string {
        switch (status) {
            case AssetCondition.Active:
                return 'bg-success-subtle text-success';
            case AssetCondition.Inactive:
                return 'bg-secondary-subtle text-secondary';
            default:
                return 'bg-info-subtle text-info';
        }
    }
}
